import React from "react";
import { Pressable, View } from "react-native";
import CCText from "@/Atoms/CCText";
import { Colors } from "@/styles";
import type { ClubTournamentHighlight } from "@/Hooks/ClubsApi/types";

function formatStartDate(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Upcoming";
  const day = date.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
  const time = date.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });
  return `${day} · ${time}`;
}

export default function TournamentHighlightBar({
  tournament, onPress, variant = "compact", compactChat = false,
}: {
  tournament: ClubTournamentHighlight;
  onPress: () => void;
  variant?: "compact" | "chat";
  compactChat?: boolean;
}) {
  const isArena = tournament.tournament_type === "Circlechess_Arena";
  const format = isArena ? "Arena" : "Swiss";
  const timeControl = formatTimeControl(tournament.time_control);
  const detail = isArena
    ? tournament.duration_minutes
      ? `${tournament.duration_minutes} min arena`
      : "Continuous pairings"
    : tournament.rounds
      ? `${tournament.rounds} rounds`
      : "Swiss pairings";

  if (variant === "chat") {
    const padding = compactChat ? 12 : 16;
    const titleSize = compactChat ? 16 : 18;
    return (
      <View
        testID={`club-tournament-card-${tournament.id}`}
        style={{
          // Tournament cards are club messages, so align and size them exactly
          // like an incoming challenge instead of filling the whole timeline.
          alignSelf: "flex-start",
          width: compactChat ? "90%" : "50%",
          backgroundColor: "#FFFFFF",
          borderRadius: 14,
          borderWidth: 1,
          borderColor: "#D9E6E3",
          borderLeftWidth: 4,
          borderLeftColor: "#3DAB9E",
          padding,
          marginBottom: 11,
          shadowColor: "#173F3A",
          shadowOffset: {width: 0, height: 2},
          shadowOpacity: 0.06,
          shadowRadius: 5,
          elevation: 1,
        }}>
        <View style={{flexDirection: "row", alignItems: "center", gap: 10}}>
          <View style={{width: compactChat ? 34 : 38, height: compactChat ? 34 : 38, borderRadius: 10, alignItems: "center", justifyContent: "center", backgroundColor: "#E5F4F1"}}>
            <CCText style={{fontSize: compactChat ? 17 : 19}}>🏆</CCText>
          </View>
          <View style={{flex: 1, minWidth: 0}}>
            <CCText style={{fontSize: 10.5, fontWeight: "700", color: "#28796E", letterSpacing: 0.5}}>TOURNAMENT · CLUB MEMBERS</CCText>
            <CCText numberOfLines={1} style={{fontSize: titleSize, fontWeight: "700", color: "#262322", marginTop: 2}}>{tournament.name}</CCText>
          </View>
        </View>
        <View style={{marginTop: compactChat ? 10 : 13, backgroundColor: "#F4F8F7", borderRadius: 10, paddingVertical: compactChat ? 8 : 10, paddingHorizontal: 10}}>
          <CCText style={{fontSize: compactChat ? 12 : 13, fontWeight: "700", color: "#344A46"}}>Starts {formatStartDate(tournament.start_date)}</CCText>
          <CCText style={{fontSize: compactChat ? 11.5 : 12.5, color: "#62736F", marginTop: 2}}>{format} · {detail}{timeControl ? ` · ${timeControl}` : ""}</CCText>
        </View>
        <Pressable testID={`club-tournament-join-${tournament.id}`} onPress={onPress} style={{marginTop: compactChat ? 10 : 12, backgroundColor: "#3DAB9E", borderRadius: 10, paddingVertical: compactChat ? 9 : 11, alignItems: "center"}}>
          <CCText style={{fontSize: compactChat ? 13 : 14, fontWeight: "700", color: "#FFFFFF"}}>View tournament & join →</CCText>
        </Pressable>
      </View>
    );
  }

  return (
    <Pressable testID="club-tournament-highlight" onPress={onPress}
      style={{ flexDirection: "row", alignItems: "center", gap: 10, padding: 12,
        borderRadius: 12, backgroundColor: Colors.primaryLight,
        borderWidth: 1, borderColor: Colors.primaryAction }}>
      <CCText style={{ fontSize: 16 }}>🏆</CCText>
      <View style={{ flex: 1 }}>
        <CCText style={{ fontSize: 14, fontWeight: "700", color: Colors.primaryDark }}>
          {tournament.name}
        </CCText>
        <CCText style={{ fontSize: 11.5, color: Colors.secondaryAction }}>
          {format} · {formatStartDate(tournament.start_date)}
        </CCText>
      </View>
      <CCText style={{ fontSize: 12.5, fontWeight: "600", color: Colors.secondaryAction }}>Join →</CCText>
    </Pressable>
  );
}

function formatTimeControl(value?: string) {
  if (!value) return null;
  const [base, increment] = value.split("+");
  const seconds = Number(base);
  if (!Number.isFinite(seconds)) return value;
  const minutes = seconds >= 60 ? seconds / 60 : seconds;
  return `${Number.isInteger(minutes) ? minutes : minutes.toFixed(1)}+${increment ?? "0"}`;
}
